import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { format } from "date-fns";
import { TailSpin } from "react-loader-spinner";

import type { AdminSignupsDailyEntry } from "../../../lib/api";

type Props = {
  daily: AdminSignupsDailyEntry[] | undefined;
  isLoading?: boolean;
};

const AXIS_LABEL = {
  fontSize: "12px",
  fontFamily: "Manrope, sans-serif",
  colors: "#617889",
};

const SignupsChart: React.FC<Props> = ({ daily, isLoading }) => {
  const points = useMemo(() => daily ?? [], [daily]);

  const { series, options } = useMemo(() => {
    const categories = points.map((d) =>
      format(new Date(`${d.date}T00:00:00`), "MMM d")
    );
    const opts: ApexOptions = {
      chart: {
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "Manrope, sans-serif",
        background: "transparent",
      },
      plotOptions: {
        bar: {
          columnWidth: points.length > 45 ? "80%" : "55%",
          borderRadius: 3,
        },
      },
      dataLabels: { enabled: false },
      legend: {
        position: "top",
        horizontalAlign: "right",
        labels: { colors: "#AEBCC6" },
        markers: { offsetX: -4 },
        itemMargin: { horizontal: 10 },
      },
      grid: { borderColor: "#2C363E", strokeDashArray: 4 },
      tooltip: { shared: true, intersect: false, theme: "dark" },
      xaxis: {
        categories,
        tickAmount: Math.min(points.length, 10),
        labels: { rotate: 0, hideOverlappingLabels: true, style: AXIS_LABEL },
        axisBorder: { color: "#2C363E" },
        axisTicks: { show: false },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: {
          style: AXIS_LABEL,
          formatter: (v: number) => `${Math.round(v)}`,
        },
      },
      states: { hover: { filter: { type: "lighten", value: 0.08 } } },
    };
    return {
      series: [
        { name: "Signups", data: points.map((d) => d.count), color: "#2385DF" },
        {
          name: "Verified",
          data: points.map((d) => d.verifiedCount),
          color: "#34D399",
        },
      ],
      options: opts,
    };
  }, [points]);

  return (
    <section className="rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:p-5">
      <h2 className="font-sora text-[16px] font-semibold text-white">
        Daily signups
      </h2>
      <p className="mt-1 text-[13px] text-[#869AA9]">
        New accounts per day, and how many of them got verified.
      </p>
      <div className="mt-2 min-h-[280px]">
        {isLoading && !daily ? (
          <div className="flex h-[280px] items-center justify-center">
            <TailSpin color="#0B75C2" height={32} width={32} />
          </div>
        ) : points.length === 0 ? (
          <div className="flex h-[280px] items-center justify-center text-[13px] text-[#869AA9]">
            No signups in this range.
          </div>
        ) : (
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={280}
          />
        )}
      </div>
    </section>
  );
};

export default SignupsChart;
